import React from 'react';
import {withSystem, Expressions} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAtAction} from '@packages/systems/ExpressionEditor-utils';
import {TRecord, TText, TBoolean, EText} from '@packages/systems/wfdl-ast';
import {FinePrint} from '@packages/systems/design-system/Text';
import {FlexRow, FlexColumn} from '@packages/systems/design-system/Layout';
import {styled} from '@packages/utilities/styled';
import {
  FORM_TYPE_ERROR_PATH,
  FORM_GENERIC_ERROR_PATH,
  FORM_TOO_LARGE_ERROR_PATH,
  FORM_TOO_SMALL_ERROR_PATH,
  FORM_REQUIRED_ERROR_PATH,
  USER_FILE_UPLOAD_ERRORS,
} from '@packages/systems/users/constants';
import MultiErrorMessagePanel from '../settings/MultiErrorMessagePanel';
import {RadioIcon, Label, ErrorItem} from '../settings/ErrorItem';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';

const StyledFinePrint = styled(FinePrint)(({theme: {gutters}}) => ({
  paddingBottom: gutters.x8,
}));

const fields = {
  users: TRecord({
    field: TText,
  }),
  form: TRecord({
    type: withSystem(
      {defaultValue: 'file-upload-error-msg', private: true},
      TText
    ),
    TOO_LARGE_ERROR: withSystem(
      {
        defaultValue: USER_FILE_UPLOAD_ERRORS.TOO_LARGE.msg,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
    TOO_SMALL_ERROR: withSystem(
      {
        defaultValue: USER_FILE_UPLOAD_ERRORS.TOO_SMALL.msg,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
    TYPE_ERROR: withSystem(
      {
        defaultValue: USER_FILE_UPLOAD_ERRORS.TYPE.msg,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
    GENERIC_ERROR: withSystem(
      {
        defaultValue: USER_FILE_UPLOAD_ERRORS.GENERIC.msg,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
    REQUIRED_ERROR: withSystem(
      {
        defaultValue: USER_FILE_UPLOAD_ERRORS.REQUIRED.msg,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
  }),
  text: withSystem({defaultValue: true, private: true}, TBoolean),
  temp: TRecord({
    state: withSystem(
      {private: true, defaultValue: USER_FILE_UPLOAD_ERRORS.GENERIC.id},
      TText
    ),
  }),
} as const;

const selectShouldOpenMiniSettings = (state: DesignerStores) =>
  state.UiStore.miniSettingsFlags.FormFileUploadErrorMsg;

const FormFileUploadErrorMsgType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function RenderFormFileUploadErrorMsgType({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: (arg1?: any) => void;
  }) {
    const strippedExpr = stripAll(expr);
    const isMiniSettingsOpen = useDesignerSelector(
      selectShouldOpenMiniSettings
    );
    const showMask = isMiniSettingsOpen ? 'TRANSPARENT' : true;
    const getValueAt = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'path' implicitly has an 'any' type. | TS7006 - Parameter 'defaultValue' implicitly has an 'any' type.
      (path, defaultValue) => {
        const initialExpr = Expressions.getIn(strippedExpr, path);
        return initialExpr ? Expressions.getText(initialExpr) : defaultValue;
      },
      [strippedExpr]
    );

    const [requiredErrMsg, setRequiredErrMsg] = React.useState(
      getValueAt(FORM_REQUIRED_ERROR_PATH, USER_FILE_UPLOAD_ERRORS.REQUIRED.msg)
    );

    const [tooSmallErrorMsg, setTooSmallErrorMsg] = React.useState(
      getValueAt(
        FORM_TOO_SMALL_ERROR_PATH,
        USER_FILE_UPLOAD_ERRORS.TOO_SMALL.msg
      )
    );
    const [tooLargeErrorMsg, setTooLargeErrorMsg] = React.useState(
      getValueAt(
        FORM_TOO_LARGE_ERROR_PATH,
        USER_FILE_UPLOAD_ERRORS.TOO_LARGE.msg
      )
    );
    const [typeErrorMsg, setTypeErrorMsg] = React.useState(
      getValueAt(FORM_TYPE_ERROR_PATH, USER_FILE_UPLOAD_ERRORS.TYPE.msg)
    );
    const [genericErrorMsg, setGenericErrorMsg] = React.useState(
      getValueAt(FORM_GENERIC_ERROR_PATH, USER_FILE_UPLOAD_ERRORS.GENERIC.msg)
    );
    const [selectedId, setSelectedId] = React.useState(
      getValueAt(TEMP_STATE_PATH, USER_FILE_UPLOAD_ERRORS.GENERIC.id)
    );
    React.useEffect(
      () =>
        setSelectedId(
          getValueAt(TEMP_STATE_PATH, USER_FILE_UPLOAD_ERRORS.GENERIC.id)
        ),
      [getValueAt]
    );

    const [errorItemHoveredId, setErrorItemHoveredId] = React.useState('');

    const errors = [
      {
        id: USER_FILE_UPLOAD_ERRORS.GENERIC.id,
        path: FORM_GENERIC_ERROR_PATH,
        title: 'Generic error',
        message: genericErrorMsg,
        setMessage: setGenericErrorMsg,
        messageDefault: USER_FILE_UPLOAD_ERRORS.GENERIC.msg,
      },
      {
        id: USER_FILE_UPLOAD_ERRORS.TOO_LARGE.id,
        path: FORM_TOO_LARGE_ERROR_PATH,
        title: 'File size: too large',
        message: tooLargeErrorMsg,
        setMessage: setTooLargeErrorMsg,
        messageDefault: USER_FILE_UPLOAD_ERRORS.TOO_LARGE.msg,
      },
      {
        id: USER_FILE_UPLOAD_ERRORS.TOO_SMALL.id,
        path: FORM_TOO_SMALL_ERROR_PATH,
        title: 'File size: too small',
        message: tooSmallErrorMsg,
        setMessage: setTooSmallErrorMsg,
        messageDefault: USER_FILE_UPLOAD_ERRORS.TOO_SMALL.msg,
      },
      {
        id: USER_FILE_UPLOAD_ERRORS.TYPE.id,
        path: FORM_TYPE_ERROR_PATH,
        title: 'Invalid file type',
        message: typeErrorMsg,
        setMessage: setTypeErrorMsg,
        messageDefault: USER_FILE_UPLOAD_ERRORS.TYPE.msg,
      },
      {
        id: USER_FILE_UPLOAD_ERRORS.REQUIRED.id,
        path: FORM_REQUIRED_ERROR_PATH,
        title: 'Upload required',
        message: requiredErrMsg,
        setMessage: setRequiredErrMsg,
        messageDefault: USER_FILE_UPLOAD_ERRORS.REQUIRED.msg,
      },
    ];

    const onCommit = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'error' implicitly has an 'any' type.
      (value: string, error) => {
        dispatch(updateExpressionAtAction(EText(value), error.path));
        error.setMessage(value);
      },
      [dispatch]
    );
    const onClick = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'error' implicitly has an 'any' type.
      (error) => {
        setSelectedId(error.id);
        dispatch(updateExpressionAtAction(EText(error.id), TEMP_STATE_PATH));
      },
      [dispatch]
    );
    return (
      <FlexColumn shrink={0}>
        <FlexRow justify="between">
          <StyledFinePrint>Error Messages:</StyledFinePrint>
        </FlexRow>

        <div data-automation-id="fileupload-errors-list">
          {errors.map((error, index) => {
            const selected = error.id === selectedId;
            const hovered = errorItemHoveredId === error.id;
            const label = <Label title={error.title} />;
            const icon = <RadioIcon selected={selected} />;
            return (
              <MultiErrorMessagePanel
                key={index}
                error={error}
                onCommit={(value) => onCommit(value, error)}
                dataAutomationIdPrefix="fileupload-errors"
                showMask={showMask}
              >
                {/* @ts-expect-error - TS7031 - Binding element 'Anchor' implicitly has an 'any' type. | TS7031 - Binding element 'openPopover' implicitly has an 'any' type. */}
                {({Anchor, openPopover}) => (
                  <Anchor asChild={false}>
                    {(anchorRef: React.RefObject<HTMLDivElement>) => (
                      <ErrorItem
                        key={index}
                        _ref={anchorRef}
                        label={label}
                        icon={icon}
                        first={index === 0}
                        last={index === errors.length - 1}
                        selected={selected}
                        onClick={() => onClick(error)}
                        onEditClick={openPopover}
                        data-automation-id="fileupload-errors-edit"
                        onMouseEnter={() => setErrorItemHoveredId(error.id)}
                        onMouseLeave={() => setErrorItemHoveredId('')}
                        itemHover={hovered}
                      />
                    )}
                  </Anchor>
                )}
              </MultiErrorMessagePanel>
            );
          })}
        </div>
      </FlexColumn>
    );
  },
});

export default FormFileUploadErrorMsgType;
